import React, { useMemo } from 'react';
import { Loader2, Percent, GitCompareArrows } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell, LabelList,
} from 'recharts';
import { useGetKvsResult9n11PassAnalyticsQuery } from '../services/kvsResult9n11Api';
import type { PassMetrics9n11 } from '../services/kvsResult9n11Api';
import SchoolPassPercentTable from './SchoolPassPercentTable';
import type { SchoolPassRow } from './SchoolPassPercentTable';

/**
 * Pass-percentage analytics for the Secondary stage (classes 9 & 11):
 * metric cards, region-wise Class IX vs XI chart, class-11 stream comparison
 * and a region-wise table. Self-loading — host screens just pass year/term.
 */

const CLASS9_COLOR = '#6366F1';
const CLASS11_COLOR = '#F59E0B';
const STREAM_COLORS: Record<string, string> = { Science: '#3B82F6', Commerce: '#10B981', Humanities: '#8B5CF6' };

const passPctText = (pct: number | null | undefined): string =>
  pct === null || pct === undefined ? '—' : `${pct.toFixed(2)}%`;

const passPctColor = (pct: number | null | undefined): string => {
  if (pct === null || pct === undefined) return 'text-gray-300';
  if (pct >= 90) return 'text-emerald-600';
  if (pct >= 75) return 'text-indigo-600';
  if (pct >= 60) return 'text-amber-600';
  return 'text-red-600';
};

const PassMetricCard: React.FC<{ label: string; accent: string; m: PassMetrics9n11 | null | undefined }> = ({ label, accent, m }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
    <div className="flex items-center justify-between">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
      <span className={`w-2.5 h-2.5 rounded-full ${accent}`} />
    </div>
    <p className={`text-3xl font-bold mt-2 ${passPctColor(m?.passPercentage)}`}>{passPctText(m?.passPercentage)}</p>
    <p className="text-[11px] text-gray-400 mt-0.5">Pass percentage · declared results only</p>
    <div className="grid grid-cols-4 gap-2 mt-4 text-center">
      <div><p className="text-sm font-semibold text-gray-900">{(m?.totalStudents ?? 0).toLocaleString()}</p><p className="text-[10px] text-gray-400 uppercase">Students</p></div>
      <div><p className="text-sm font-semibold text-emerald-700">{(m?.passed ?? 0).toLocaleString()}</p><p className="text-[10px] text-gray-400 uppercase">Pass</p></div>
      <div><p className="text-sm font-semibold text-red-600">{(m?.failed ?? 0).toLocaleString()}</p><p className="text-[10px] text-gray-400 uppercase">Fail</p></div>
      <div><p className="text-sm font-semibold text-amber-600">{(m?.notDeclared ?? 0).toLocaleString()}</p><p className="text-[10px] text-gray-400 uppercase" title="Result not declared — excluded from Pass %">ND</p></div>
    </div>
  </div>
);

interface Props {
  year: number;
  /** Sent to the API (0 = overall). The 9/11 result data has no term dimension. */
  term?: number;
  onRegionClick?: (roID: number) => void;
}

const PassPercentAnalytics: React.FC<Props> = ({ year, term = 0, onRegionClick }) => {
  const { data: passRes, isLoading } = useGetKvsResult9n11PassAnalyticsQuery({ year, term });
  const pass = passRes?.data;

  const regionChartData = useMemo(() => {
    return (pass?.regions ?? [])
      .map(r => ({
        name: r.roName ?? `RO ${r.roID}`,
        roID: r.roID,
        class9: r.byClass?.['9']?.passPercentage ?? null,
        class11: r.byClass?.['11']?.passPercentage ?? null,
        overall: r.passPercentage,
      }))
      .sort((a, b) => (b.overall ?? -1) - (a.overall ?? -1));
  }, [pass]);

  const streamChartData = useMemo(() => {
    return (pass?.streams?.rows ?? []).map(s => ({
      name: s.streamName,
      passPct: s.passPercentage,
      totalStudents: s.totalStudents,
      passed: s.passed,
      failed: s.failed,
      notDeclared: s.notDeclared,
      avgMarks: s.avgMarks,
    }));
  }, [pass]);

  const streamCoverage = pass?.streams?.coverage;
  const streamCoveragePct = streamCoverage && streamCoverage.total > 0
    ? ((streamCoverage.matched / streamCoverage.total) * 100).toFixed(1)
    : null;

  // Region rows reuse the pass-percent table: kvID/schoolName carry roID/roName.
  const regionPassRows: SchoolPassRow[] = useMemo(() => {
    return (pass?.regions ?? []).map(r => ({
      kvID: r.roID,
      schoolID: r.roID,
      schoolName: r.roName,
      totalStudents: r.totalStudents,
      avgMarks: r.avgMarks,
      passed: r.passed,
      failed: r.failed,
      notDeclared: r.notDeclared,
      passPercentage: r.passPercentage,
      byClass: r.byClass,
    }));
  }, [pass]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
        <span className="ml-3 text-sm text-gray-500">Loading pass percentage analytics…</span>
      </div>
    );
  }

  if (!pass) return null;

  return (
    <>
      <div className="flex items-center gap-2 pt-2">
        <Percent size={18} className="text-indigo-600" />
        <h2 className="text-lg font-semibold text-gray-900">Pass Percentage Analytics</h2>
        <span className="text-xs text-gray-400">Classes IX &amp; XI · Pass % = Pass / (Pass + Fail), not-declared excluded</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PassMetricCard label="Overall — Secondary Stage" accent="bg-gray-400" m={pass.overall} />
        <PassMetricCard label="Class IX" accent="bg-indigo-500" m={pass.byClass?.['9']} />
        <PassMetricCard label="Class XI" accent="bg-amber-500" m={pass.byClass?.['11']} />
      </div>

      {regionChartData.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
            <div>
              <h3 className="text-sm font-semibold text-gray-700">Region-wise Pass Percentage — Class IX vs Class XI</h3>
              <p className="text-xs text-gray-400">Sorted by overall pass % · hover a bar for details</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div style={{ minWidth: Math.max(900, regionChartData.length * 56) }}>
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={regionChartData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                  <XAxis dataKey="name" angle={-40} textAnchor="end" interval={0} height={90} tick={{ fontSize: 11, fill: '#6B7280' }} />
                  <YAxis domain={[0, 100]} unit="%" width={44} tick={{ fontSize: 11, fill: '#6B7280' }} />
                  <Tooltip
                    formatter={(value: any, name: any) => [value == null ? '—' : `${Number(value).toFixed(2)}%`, name]}
                    labelStyle={{ fontWeight: 600 }}
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="class9" name="Class IX" fill={CLASS9_COLOR} radius={[4, 4, 0, 0]} maxBarSize={18} />
                  <Bar dataKey="class11" name="Class XI" fill={CLASS11_COLOR} radius={[4, 4, 0, 0]} maxBarSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {streamChartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-1">
              <GitCompareArrows size={15} className="text-indigo-600" />
              <h3 className="text-sm font-semibold text-gray-700">Class XI — Stream-wise Pass Percentage</h3>
            </div>
            <p className="text-xs text-gray-400 mb-2">
              Science vs Commerce vs Humanities — the finest curriculum split available (no per-subject marks for classes IX/XI)
            </p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={streamChartData} margin={{ top: 24, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#6B7280' }} />
                <YAxis domain={[0, 100]} unit="%" width={44} tick={{ fontSize: 11, fill: '#6B7280' }} />
                <Tooltip
                  formatter={(value: any) => (value == null ? '—' : `${Number(value).toFixed(2)}%`)}
                  labelStyle={{ fontWeight: 600 }}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
                <Bar dataKey="passPct" name="Pass %" radius={[4, 4, 0, 0]} maxBarSize={64}>
                  <LabelList dataKey="passPct" position="top" formatter={(v: any) => (v == null ? '—' : `${Number(v).toFixed(1)}%`)} style={{ fontSize: 12, fill: '#374151' }} />
                  {streamChartData.map(s => (
                    <Cell key={s.name} fill={STREAM_COLORS[s.name] ?? '#6366F1'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">Class XI — Stream Comparison</h3>
            </div>
            <div className="overflow-x-auto flex-1">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase">Stream</th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase">Students</th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase">Pass</th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase">Fail</th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase" title="Result not declared">ND</th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase">Avg Marks</th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase">Pass %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {streamChartData.map(s => (
                    <tr key={s.name}>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STREAM_COLORS[s.name] ?? '#6366F1' }} />
                          {s.name}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-sm text-gray-700">{s.totalStudents.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-sm text-emerald-700">{s.passed.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-sm text-red-600">{s.failed.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-sm text-amber-600">{s.notDeclared.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-sm text-gray-700">{s.avgMarks.toFixed(1)}</td>
                      <td className={`px-4 py-2.5 text-right text-sm font-semibold ${passPctColor(s.passPct)}`}>{passPctText(s.passPct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {streamCoveragePct && (
              <div className="px-5 py-2.5 bg-gray-50 border-t border-gray-100">
                <p className="text-[11px] text-gray-500">
                  Stream recorded for {streamCoverage!.matched.toLocaleString()} of {streamCoverage!.total.toLocaleString()} class-XI students ({streamCoveragePct}%). Students without a recorded stream are excluded from this comparison.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {regionPassRows.length > 0 && (
        <SchoolPassPercentTable
          schools={regionPassRows}
          classes={[9, 11]}
          stageLabel="Secondary Stage"
          regionName="All Regions"
          year={year}
          entityLabel="Region"
          idLabel="RO ID"
          totalRowLabel="National Total"
          sourceNote="Source: kvs_mis.student_previous_result_details, aggregated per Regional Office (master_ros)."
          onRowClick={onRegionClick ? (row) => onRegionClick(row.kvID) : undefined}
        />
      )}
    </>
  );
};

export default PassPercentAnalytics;
